import type { BillingSubscriptionStatus } from '../enums/billing-subscription-status.enum.js'
import type { BillingState, LockReason } from '../enums/billing-state.enum.js'
import {
  GRACE_DAYS,
  PRICE_PER_VENUE_CENTS,
  TRIAL_CANCELLED_RETENTION_DAYS,
} from '../constants/billing.js'

/**
 * What `billingStateOf` reads. Mirrors the billing columns on `tenants`.
 * The states it returns are listed in `lib/src/enums/billing-state.enum.ts`.
 */
export interface BillingFacts {
  subscriptionStatus: BillingSubscriptionStatus
  trialEndsAt: Date | null
  currentPeriodEndsAt: Date | null
  cancelAtPeriodEnd: boolean
  pastDueSince: Date | null
  graceExtendedDays: number
  trialCancelledAt: Date | null
  compedAt: Date | null
  compEndsAt: Date | null
}

/**
 * A company's standing, with only the facts that exist in that state.
 *
 * A lock reason cannot be read off a company that is not locked, a grace end
 * cannot be missing while payments are failing, and a deletion date exists only
 * after a canceled trial. The flat shape this replaced let those combinations
 * compile, so every caller null-checked a value the state machine had already
 * decided.
 */
export type BillingStanding =
  | { state: 'signup_incomplete' }
  | { state: 'trialing' }
  | { state: 'active' }
  | { state: 'cancelling' }
  | { state: 'past_due'; graceEndsAt: Date }
  | { state: 'locked'; lockReason: 'non_payment'; graceEndsAt: Date }
  | { state: 'locked'; lockReason: Exclude<LockReason, 'non_payment'> }
  | { state: 'trial_cancelled'; deletesAt: Date }
  | { state: 'comped' }
  | { state: 'comp_ending' }

const DAY_MS = 24 * 60 * 60 * 1000

/** A subscription Stripe is still billing — trialing, paid, or behind on payment. */
export function isLiveSubscription(status: BillingSubscriptionStatus): boolean {
  return status === 'trialing' || status === 'active' || status === 'past_due'
}

function addDays(at: Date, days: number): Date {
  return new Date(at.getTime() + days * DAY_MS)
}

/**
 * A company's billing state at `now`, from its stored facts.
 *
 * Worked out rather than stored, so a deadline passing — a grace period ending,
 * a comp notice running out — takes effect on the next request with no job to
 * run and none to fail. Stripe's reported status is the fact about money; the
 * grace length, comps and trial cancellation are the app's, layered on top.
 *
 * The order matters:
 * 1. A comp that has not been ended wins over everything else — a live
 *    subscription, and a canceled trial too: a comped company is never billed,
 *    never locked and never deleted, even in the moment before Stripe reports
 *    its subscription canceled. Comping a company mid-trial is how a super
 *    admin rescues it, not a way to have it deleted.
 * 2. A canceled trial is final otherwise.
 * 3. A live subscription (trialing, active, past due) is what it says — which
 *    also covers a company whose comp is ending and has subscribed.
 * 4. An ending comp gives its notice, then locks.
 * 5. Otherwise the subscription decides: never subscribed is an unfinished
 *    signup; canceled is locked.
 *
 * Deadlines the app owns — the grace period, a comp's notice, a cancellation's
 * paid-through date — are enforced here by the clock. A trial's end is not: the
 * card may well be charged successfully, and locking a paying company because a
 * webhook was late would be the worse mistake. Stripe reports how a trial ended.
 */
export function billingStateOf(facts: BillingFacts, now: Date): BillingStanding {
  if (facts.compedAt !== null && facts.compEndsAt === null) return { state: 'comped' }

  if (facts.trialCancelledAt !== null) {
    return {
      state: 'trial_cancelled',
      deletesAt: addDays(facts.trialCancelledAt, TRIAL_CANCELLED_RETENTION_DAYS),
    }
  }

  const status = facts.subscriptionStatus
  const live = isLiveSubscription(status)

  if (!live && facts.compedAt !== null && facts.compEndsAt !== null) {
    if (now < facts.compEndsAt) return { state: 'comp_ending' }
    return { state: 'locked', lockReason: 'comp_ended' }
  }

  switch (status) {
    case 'trialing':
      return { state: 'trialing' }
    case 'active':
      if (!facts.cancelAtPeriodEnd) return { state: 'active' }
      // Paid through the end of the period and no further: once it passes, the
      // company is locked whether or not Stripe's "deleted" event has arrived.
      if (facts.currentPeriodEndsAt !== null && now >= facts.currentPeriodEndsAt) {
        return { state: 'locked', lockReason: 'cancelled' }
      }
      return { state: 'cancelling' }
    case 'past_due': {
      // The sync records when failures began. If it somehow has not, the end of
      // the paid period is when the failed invoice was due — a fixed point, so
      // the grace period still ends. Only with neither does it start from now.
      const graceStart = facts.pastDueSince ?? facts.currentPeriodEndsAt ?? now
      const graceEndsAt = addDays(graceStart, GRACE_DAYS + facts.graceExtendedDays)
      return now < graceEndsAt
        ? { state: 'past_due', graceEndsAt }
        : { state: 'locked', lockReason: 'non_payment', graceEndsAt }
    }
    case 'canceled':
      return { state: 'locked', lockReason: 'cancelled' }
    case 'none':
      return { state: 'signup_incomplete' }
  }
}

/**
 * States in which a company's people can reach only what gets them out: the
 * billing owner's billing screen, and a lock screen for everyone else. Outside
 * access — clients, share links, public pages, API tokens — stops entirely.
 */
export function isRestricted(state: BillingState): boolean {
  return state === 'signup_incomplete' || state === 'locked' || state === 'trial_cancelled'
}

/**
 * What a company contributes to monthly recurring revenue, in cents: its venues
 * at the price, while it is paying. Everything else — trialing, past due,
 * comped, locked — is $0, as the super admin asked.
 */
export function monthlyRecurringCents(state: BillingState, activeVenues: number): number {
  return state === 'active' || state === 'cancelling' ? activeVenues * PRICE_PER_VENUE_CENTS : 0
}
